import { useEffect, useRef } from 'react'

interface TextareaProps {
	readonly onChange: (value: string) => void;
	readonly value: string;
	readonly disabled?: boolean | undefined;
	readonly onKeyDown?: ((event: React.KeyboardEvent<HTMLTextAreaElement>) => void) | undefined;
	readonly label?: string | undefined;
	readonly placeholder?: string | undefined;
	readonly style?: 'default' | 'naked' | undefined;
	readonly rezisable?: boolean | undefined;
	readonly maxHeight?: number | undefined;
	readonly customClass?: string | undefined;
}

export default function Textarea({
	onChange,
	value = '',
	disabled = false,
	onKeyDown,
	label,
	placeholder,
	style = 'default',
	rezisable = false,
	maxHeight = 300,
	customClass
}: TextareaProps) {
	const textAreaRef = useRef<HTMLTextAreaElement>(null)

	function useAutosizeTextArea(value: string) {
		useEffect(() => {
			const ref = textAreaRef.current

			if (ref) {
				ref.style.height = '0px'
				const { scrollHeight } = ref

				if (scrollHeight > maxHeight) {
					ref.style.height = maxHeight.toString() + 'px'
					ref.style.overflowY = 'scroll'
				} else {
					ref.style.height = scrollHeight.toString() + 'px'
				}
			}
		}, [textAreaRef, value])
	}

	function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
		const { value } = event.target

		onChange(value)
	}

	useAutosizeTextArea(value)

	const styleClasses = {
		default: 'border border-solid border-gray-300 rounded-md px-3 py-2',
		naked: 'border-0'
	}

	return (
		<div className='flex flex-col'>
			{
				label && (
					<label className='text-gray-500 text-sm mb-'>
						{ label }
					</label>
				)
			}
			<textarea
				ref={ textAreaRef }
				tabIndex={ 0 }
				rows={ 1 }
				disabled={ disabled }
				placeholder={ placeholder }
				className={
					`m-0 w-full rounded-md focus:ring-0 focus-visible:ring-0 outline-none bg-transparent max-h-[20dvh]
					${rezisable ? '' : 'resize-none'}
					${styleClasses[style]}
					${customClass ?? ''}
					`
				}
				value={ value }
				onChange={ handleChange }
				onKeyDown={ onKeyDown }
			/>
		</div>
	)
}
